// Shared video-vs-image detection (UI Regression Defense, REF-6063 follow-through).
//
// The repo grew competing conventions for "is this URL a video?", and the bare
// `.endsWith(".mp4")` variants silently break on signed GCS URLs whose query string
// follows the extension. This module is the canonical home.
//
// The migration is DONE: every bare `.endsWith(".mp4" | ".webm" | ".mov" | ".m4v")`
// call site in src/ now routes through here — a TypeScript-AST count on the pre-migration
// tree found 16 calls across 10 files (13 source lines), in the Studio detail page and
// modal, JOOR export gating, and six Design Canvas files (AssetNode, GenerationNode,
// DesignCanvas's selection + node context menu, EditThumbnails, ResultLayout,
// canvasFeedbackGate). `npm run check:video-detection` holds that at ZERO — use it rather
// than a raw grep, which also matches the explanatory comments this migration left behind
// and three test files that deliberately assert the bare form gives the WRONG answer.
// Keep it at zero: a new bare check is a new signed-URL bug, not a style nit.
//
// `isVideoThumbnail` is shaped exactly like the inline helper REF-6063 added to
// EditDetailThumbnailStrip (on dev/stage): when that commit reaches main, its
// inline helper swaps to this import with zero behavior change.

const VIDEO_EXTENSIONS = [".mp4", ".webm", ".mov", ".m4v"];

export const isVideoUrl = (url: string | undefined | null): boolean => {
  if (typeof url !== "string" || url.length === 0) return false;

  const path = url.split("?")[0].split("#")[0].toLowerCase();

  return VIDEO_EXTENSIONS.some((ext) => path.endsWith(ext));
};

export const isVideoThumbnail = (imageType: string | undefined, path: string | undefined | null): boolean => imageType === "video_edit" || isVideoUrl(path);

// Containers and documents no browser can EVER decode as an image. Deliberately conservative:
// only extensions this app actually produces or accepts as uploads. `.svg` is absent because an
// <img> renders it fine, and video extensions are absent on purpose — a video url reaching an
// image element is `[VIDEO_URL_DETECTION]`'s problem and has its own handling, so folding it in
// here would silently change what those consumers do today.
const NON_IMAGE_EXTENSIONS = [".zip", ".rar", ".7z", ".tar", ".gz", ".pdf", ".xlsx", ".xls", ".csv", ".doc", ".docx", ".lnk", ".psd", ".ai"];

/**
 * True when a url CANNOT be an image, judged from its path extension alone.
 *
 * The point is the asymmetry with a normal load failure: a 404 or a 503 is worth retrying, and
 * `RetryImg` exists to retry it. An archive is not — the request succeeds, the bytes arrive, and
 * the decoder rejects them every single time, so 11 attempts over 12 seconds buy nothing but 11
 * requests and a Sentry warning. `false` here means "might be an image", never "is one": the real
 * offender in Sentry 7656627839's moodboard event was a Windows `.lnk` NAMED `.png`, and no
 * extension check can catch that — only decoding the bytes before upload can (`useFileValidation`).
 *
 * Path only, for the signed-url reason spelled out on `isVideoUrl` above.
 */
export const isNonImageUrl = (url: string | undefined | null): boolean => {
  if (typeof url !== "string" || url.length === 0) return false;

  const path = url.split("?")[0].split("#")[0].toLowerCase();

  return NON_IMAGE_EXTENSIONS.some((ext) => path.endsWith(ext));
};

// A stringified JSON payload that reached a url slot. No url starts with either character, and both
// shapes show up in this codebase as raw BE fields (`image_path` carrying `[{"imageUrl": …}]` for a
// crawled product row) handed straight to an <img>.
const JSON_PAYLOAD_PREFIXES = ["[", "{"];

/**
 * True when a src is not a url AT ALL — blank, whitespace-only, or a stringified JSON payload.
 *
 * Distinct from `isNonImageUrl`, which judges a real url by its extension. This one catches the
 * value that never was a url, and it matters because of what the browser does with it: React emits
 * a literal `src=""`, the browser resolves it against the DOCUMENT url and fetches the SPA's
 * index.html, the decoder rejects the HTML, and `RetryImg` then spends its whole budget re-fetching
 * the page — 11 requests and ~12s of blank tile to reach a conclusion available at once.
 *
 * It is also why Sentry 7692940189 was unreadable: every such failure reports
 * `hostname: app.refabric.com, path_prefix: "/"` regardless of which of the ~50 `RetryImg` call
 * sites produced it, and landed in the same fingerprint as genuine network failures. `invalid_src`
 * splits it out. A blank src is ALWAYS a wiring bug upstream of the image element.
 */
export const isUnloadableImageSrc = (url: string | undefined | null): boolean => {
  if (typeof url !== "string") return true;

  const trimmed = url.trim();

  return trimmed.length === 0 || JSON_PAYLOAD_PREFIXES.some((prefix) => trimmed.startsWith(prefix));
};

// AbortError fires when <video> is unmounted or its src swaps mid-play (Sentry REFABRIC-FRONTEND-2F).
// NotAllowedError fires when the browser blocks autoplay. Both are expected on these UI surfaces; everything
// else propagates so the global unhandledrejection handler still reports genuine playback failures.
export const playMediaSafely = async (media: HTMLMediaElement): Promise<boolean> => {
  try {
    await media.play();

    return true;
  } catch (err) {
    if (err instanceof Error && (err.name === "AbortError" || err.name === "NotAllowedError")) {
      return false;
    }
    throw err;
  }
};
